import { useClickOutside } from '@hooks';
import CollapseIcon from '@icons/collapse.svg?react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import Styled from './Select.styled';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
  endIcon?: ReactNode;
  startIcon?: ReactNode;
  onClick?: () => void;
}

export type SelectPlacement = 'bottom' | 'top';

export interface SelectProps {
  disabled?: boolean;
  icon?: ReactNode;
  label?: string;
  options: SelectOption[];
  placeholder?: string;
  placement?: SelectPlacement;
  showLabel?: boolean;
}

function Select({
  disabled = false,
  icon,
  label,
  options,
  placeholder,
  placement = 'bottom',
  showLabel = true
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const triggerLabel = placeholder ?? label ?? 'Select option';

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleToggleMenu = useCallback(() => {
    if (disabled) {
      return;
    }

    setIsOpen((open) => !open);
  }, [disabled]);

  const handleSelectOption = useCallback(
    (option: SelectOption) => {
      if (option.disabled) {
        return;
      }

      option.onClick?.();
      closeMenu();
    },
    [closeMenu]
  );

  useClickOutside(rootRef, isOpen, closeMenu);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeMenu, isOpen]);

  return (
    <Styled.Root ref={rootRef} $showLabel={showLabel}>
      <Styled.Trigger
        disabled={disabled}
        type="button"
        $showLabel={showLabel}
        onClick={handleToggleMenu}>
        {icon ? <Styled.LeadingIcon>{icon}</Styled.LeadingIcon> : null}
        {showLabel ? <Styled.Label>{triggerLabel}</Styled.Label> : null}
        {showLabel ? (
          <Styled.TrailingIcon $expanded={isOpen}>
            <CollapseIcon />
          </Styled.TrailingIcon>
        ) : null}
      </Styled.Trigger>
      {isOpen ? (
        <Styled.Menu id={listboxId} role="listbox" $placement={placement}>
          {options.map((option) => (
            <Styled.MenuItem key={option.value} role="presentation">
              <Styled.MenuItemButton
                disabled={option.disabled}
                role="option"
                type="button"
                onClick={() => handleSelectOption(option)}>
                {option.startIcon ? (
                  <Styled.MenuItemIcon>{option.startIcon}</Styled.MenuItemIcon>
                ) : null}
                <Styled.MenuItemText>
                  <Styled.MenuItemLabel>{option.label}</Styled.MenuItemLabel>
                  {option.description ? (
                    <Styled.MenuItemDescription>
                      {option.description}
                    </Styled.MenuItemDescription>
                  ) : null}
                </Styled.MenuItemText>
                {option.endIcon ? (
                  <Styled.MenuItemIcon>{option.endIcon}</Styled.MenuItemIcon>
                ) : null}
              </Styled.MenuItemButton>
            </Styled.MenuItem>
          ))}
        </Styled.Menu>
      ) : null}
    </Styled.Root>
  );
}

export default Select;
